import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FolderOpen, FileText, FileSpreadsheet, FileDown, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FORMAT_OPTIONS = [
  { value: 'docx', label: 'DOCX', desc: 'Word', icon: FileText, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { value: 'pdf', label: 'PDF', desc: 'PDF', icon: FileDown, color: 'text-red-600 bg-red-50 border-red-200' },
  { value: 'xlsx', label: 'XLSX', desc: 'Excel', icon: FileSpreadsheet, color: 'text-green-600 bg-green-50 border-green-200' },
];

const AlfrescoExportDialog = ({ open, onClose, documentId, docTitle, onExported }) => {
  const [format, setFormat] = useState('docx');
  const [exporting, setExporting] = useState(false);
  const [exports, setExports] = useState([]);
  const [loadingExports, setLoadingExports] = useState(false);

  useEffect(() => {
    if (open && documentId) {
      setLoadingExports(true);
      axios.get(`${API_URL}/alfresco/exports/${documentId}`, { withCredentials: true })
        .then(res => setExports(res.data))
        .catch(() => {})
        .finally(() => setLoadingExports(false));
    }
  }, [open, documentId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.post(`${API_URL}/alfresco/export/${documentId}`, { format }, { withCredentials: true });
      toast.success(res.data.message);
      if (onExported) onExported();
      // Refresh exports list
      const exRes = await axios.get(`${API_URL}/alfresco/exports/${documentId}`, { withCredentials: true });
      setExports(exRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar para Alfresco');
    } finally {
      setExporting(false);
    }
  };

  const hasExportForFormat = (fmt) => exports.some(e => e.format === fmt);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="alfresco-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-[#AF2628]" />
            Exportar para Alfresco
          </DialogTitle>
          <DialogDescription>
            Enviar "{docTitle}" para o repositorio Alfresco
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <p className="text-sm font-medium text-[#111111] mb-2">Formato de exportacao</p>
            <div className="grid grid-cols-3 gap-2">
              {FORMAT_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const isSelected = format === opt.value;
                const alreadyExported = hasExportForFormat(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => setFormat(opt.value)}
                    data-testid={`alfresco-format-${opt.value}`}
                    className={`relative p-3 rounded-lg border-2 text-center transition-all ${
                      isSelected
                        ? 'border-[#AF2628] bg-[#FFF1F0]'
                        : 'border-[#E5E5EA] bg-white hover:border-[#AEAEB2]'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-1 ${isSelected ? 'text-[#AF2628]' : 'text-[#6E6E73]'}`} />
                    <span className={`text-sm font-semibold ${isSelected ? 'text-[#AF2628]' : 'text-[#111111]'}`}>
                      .{opt.value}
                    </span>
                    {alreadyExported && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {hasExportForFormat(format) && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Este formato ja foi exportado. Uma nova versao sera criada no Alfresco.
              </p>
            )}
          </div>

          {/* Export history */}
          {exports.length > 0 && (
            <div>
              <p className="text-sm font-medium text-[#111111] mb-2">Historico de exportacoes</p>
              <div className="max-h-36 overflow-y-auto space-y-1.5">
                {exports.slice(0, 5).map(exp => (
                  <div key={exp.export_id} className="flex items-center justify-between text-xs bg-[#F7F7F8] rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-[#AF2628] uppercase">{exp.format}</span>
                      <span className="text-[#6E6E73]">v{exp.version}</span>
                      {exp.auto_export && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-medium">Auto</span>}
                    </div>
                    <div className="text-[#6E6E73]">
                      {exp.exported_by_name} - {new Date(exp.exported_at).toLocaleString('pt-BR')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {loadingExports && (
            <div className="flex items-center gap-2 text-xs text-[#6E6E73]">
              <Loader2 className="w-3 h-3 animate-spin" /> Carregando historico...
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={exporting} data-testid="alfresco-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-[#AF2628] hover:bg-[#961B22]"
            data-testid="alfresco-confirm-export-btn"
          >
            {exporting ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Exportando...</>
            ) : (
              <><FolderOpen className="w-4 h-4 mr-1.5" /> Exportar .{format}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlfrescoExportDialog;
